import { atom } from "jotai";

export interface EmbeddingSearchResult {
  chunk: string;
  similarity: number;
  [key: string]: any; // Allow additional properties
}

export interface EmbeddingSearchResultWithHash extends EmbeddingSearchResult {
  hash: string;
}

export interface EmbeddingSearchOptions {
  endpoint: string;
  query: string;
  source?: string;
  maxResults?: number;
}

/**
 * Generic function to search for similar chunks via embedding API
 */
export async function searchEmbeddedContent(
  options: EmbeddingSearchOptions,
): Promise<EmbeddingSearchResult[]> {
  const { endpoint, query, source, maxResults = 3 } = options;

  try {
    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        query,
        ...(source && { source }),
      }),
    });

    if (!response.ok) {
      throw new Error(`Search request failed: ${response.statusText}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || "Search request failed");
    }

    return data.results.slice(0, maxResults);
  } catch (error) {
    console.error("Error searching embedded content:", error);
    throw error;
  }
}

/**
 * Create a simple hash function for chunks
 */
export function hashString(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(36);
}

/**
 * Normalize chunk text by removing excess whitespace
 */
export function normalizeChunk(chunk: string): string {
  return chunk
    .replace(/\n\s*/g, " ") // Replace newlines + indentation with space
    .replace(/\s{2,}/g, " ") // Replace multiple spaces with single space
    .trim(); // Remove leading/trailing whitespace
}

/**
 * Create a storage atom for embedding chunks
 */
export function createChunkStorageAtom<T extends EmbeddingSearchResult>(
  debugLabel: string,
) {
  const atom_instance = atom<Record<string, T>>({});
  atom_instance.debugLabel = debugLabel;
  return atom_instance;
}

/**
 * Process search results with hashing and storage
 */
export function processEmbeddingResults<T extends EmbeddingSearchResult>(
  results: T[],
): {
  resultsWithHashes: (T & { hash: string })[];
  chunkMap: Record<string, T>;
  summaryResults: { hash: string; similarity: number; source: string }[];
} {
  const resultsWithHashes = results.map((result) => ({
    ...result,
    hash: hashString(result.chunk),
    chunk: normalizeChunk(result.chunk),
  }));

  const chunkMap: Record<string, T> = {};
  resultsWithHashes.forEach(({ hash, ...result }) => {
    chunkMap[hash] = result as T;
  });

  const summaryResults = resultsWithHashes.map(
    ({ hash, similarity, source }) => ({
      hash,
      similarity,
      source: source,
    }),
  );

  return { resultsWithHashes, chunkMap, summaryResults };
}
